import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import '../../styles/WorkDetailModal.css';

/**
 * Work 목록 항목 클릭 시 상세 내용을 보여주는 모달.
 * - item: workItems.js 의 WORK_ITEMS 항목 (null 이면 닫힘)
 * - onClose: 닫기 콜백 (오버레이 클릭·닫기 버튼·ESC)
 * 미디어 갤러리: 메인 뷰어 + 썸네일 전환 (video / image 혼용)
 */
const WorkDetailModal = ({ item, onClose }) => {
  const [mediaIndex, setMediaIndex] = useState(0);

  // 항목이 바뀌면 갤러리를 첫 미디어로 초기화
  useEffect(() => {
    setMediaIndex(0);
  }, [item]);

  // 열려 있는 동안 배경 스크롤 잠금 + ESC 로 닫기
  useEffect(() => {
    if (!item) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [item, onClose]);

  const activeMedia = item?.media?.[mediaIndex] ?? item?.media?.[0];

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          className="work-modal__overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          onClick={onClose}
        >
          <motion.div
            className="work-modal"
            role="dialog"
            aria-modal="true"
            aria-label={item.title}
            initial={{ opacity: 0, y: 42, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 28, scale: 0.98 }}
            transition={{ duration: 0.38, ease: [0.2, 0.9, 0.25, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="work-modal__close"
              aria-label="닫기"
              onClick={onClose}
            >
              ×
            </button>

            <header className="work-modal__header">
              <span className="section-eyebrow work-modal__eyebrow">Case / Aura</span>
              <p className="work-modal__meta">
                <span className="portfolio-article-tag">{item.tag}</span>
                <span className="work-modal__meta-sep" aria-hidden="true">·</span>
                <time className="portfolio-article-date" dateTime={item.date}>
                  {item.date}
                </time>
              </p>
              <h3 className="work-modal__title">{item.title}</h3>
            </header>

            {/* --- 미디어 갤러리: 메인 뷰어 + 썸네일 --- */}
            {activeMedia && (
              <div className="work-modal__gallery">
                <div className="work-modal__stage">
                  {activeMedia.type === 'video' ? (
                    <video
                      key={activeMedia.src}
                      src={activeMedia.src}
                      controls
                      autoPlay
                      muted
                      loop
                      playsInline
                    />
                  ) : (
                    <img key={activeMedia.src} src={activeMedia.src} alt={activeMedia.label} />
                  )}
                </div>
                {item.media.length > 1 && (
                  <div className="work-modal__thumbs" role="tablist" aria-label="미디어 선택">
                    {item.media.map((m, idx) => (
                      <button
                        key={m.src}
                        type="button"
                        role="tab"
                        aria-selected={idx === mediaIndex}
                        aria-label={m.label}
                        className={
                          idx === mediaIndex
                            ? 'work-modal__thumb work-modal__thumb--active'
                            : 'work-modal__thumb'
                        }
                        onClick={() => setMediaIndex(idx)}
                      >
                        {m.type === 'video' ? (
                          <span className="work-modal__thumb-video" aria-hidden="true">
                            <video src={m.src} muted preload="metadata" playsInline />
                            <span className="work-modal__thumb-play">▶</span>
                          </span>
                        ) : (
                          <img src={m.src} alt="" loading="lazy" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <p className="work-modal__lead">{item.excerpt}</p>

            {/* --- 전시 개요 --- */}
            {item.info?.length > 0 && (
              <dl className="work-modal__info">
                {item.info.map((row) => (
                  <div key={row.label} className="work-modal__info-row">
                    <dt>{row.label}</dt>
                    <dd>{row.value}</dd>
                  </div>
                ))}
              </dl>
            )}

            {/* --- 본문 --- */}
            <div className="work-modal__body">
              <h4 className="work-modal__section-heading">전시 이야기</h4>
              {item.detailParagraphs.map((para, idx) => (
                <p key={idx} className="work-modal__paragraph">
                  {para}
                </p>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WorkDetailModal;
